import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faLeaf,
  faArrowRight,
  faTicketAlt,
  faClock,
  faUsers,
  faInfoCircle,
  faEnvelope,
  faPlus,
  faSnowflake,
  faGift,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

// Import images
import castleImage from '../../../assets/castle2.png';
import gardensImage from '../../../assets/gardens.png';
import afternoonTeaImage from '../../../assets/tearoom.jpg';
import tearoomBanner from '../../../assets/banner/tearoomBan.webp';

const AfternoonTeaContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const NewsletterBadge = styled.div`
  background: linear-gradient(135deg, #c41e3a, #228b22, #d4af37);
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-align: center;
  margin: 0 auto 2rem;
  max-width: 400px;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &::before {
    content: '✨';
    position: absolute;
    left: 10px;
    animation: sparkle 2s infinite;
  }
  
  &::after {
    content: '✨';
    position: absolute;
    right: 10px;
    animation: sparkle 2s infinite 1s;
  }
  
  @keyframes sparkle {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const SpecialOfferText = styled.div`
  background: linear-gradient(135deg, #f8f4e6, #faf6ed);
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 20px;
    font-size: 2rem;
    background: white;
    padding: 0 10px;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -15px;
    right: 20px;
    font-size: 2rem;
    background: white;
    padding: 0 10px;
  }
  
  h3 {
    color: ${props => props.theme.colors.accent};
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  p {
    color: ${props => props.theme.colors.primary};
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.accent};
  }
  
  &::before {
    content: '❄️';
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    font-size: 1.5rem;
    animation: float 3s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`;

const OptionImage = styled.div`
  height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '${props => props.badge}';
    position: absolute;
    top: 15px;
    right: 15px;
    background: ${props => props.theme.colors.accent};
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const OptionContent = styled.div`
  padding: 2rem;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2rem;
`;

const OptionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const PriceTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '${props => props.label}';
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: #d4af37;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    white-space: nowrap;
  }
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  font-size: 1.1rem;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.greyBackground}, #f8f8f8);
  padding: 2rem;
  border-radius: 12px;
  margin-top: 4rem;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.accent};
  position: relative;
  
  &::before {
    content: '🔔';
    position: absolute;
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    padding: 5px 15px;
    border-radius: 50%;
    font-size: 1.5rem;
  }
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const NewsletterFestiveAfternoonTea = () => {
  return (
    <AfternoonTeaContainer>
      <ParallaxBanner 
        title="Add Festive Afternoon Tea to Your Visit"
        subtitle="Experience the Magic of Christmas at Belvoir"
        image={tearoomBanner}
      />
      
      <ContentContainer>
        <SectionTitle>Add Festive Afternoon Tea to Your Booking</SectionTitle>
        <IntroText>
          Thank you for booking your visit to Belvoir Castle! Embrace the festive spirit by adding 
          our enchanting Christmas Afternoon Tea to your day. Choose the option that suits your booking below.
        </IntroText>
        
        <SpecialOfferText>
          <h3>🎄 Magical Christmas Experience 🎁</h3>
          <p>
            Our Festive Afternoon Tea features seasonal delicacies, Christmas spiced treats, and mulled wine options 
            in our elegantly decorated Aviary Tearoom. Experience the wonder of Christmas at Belvoir Castle.
          </p>
        </SpecialOfferText>
        
        <OptionsGrid>
          <OptionCard>
            <OptionImage image={afternoonTeaImage} badge="FESTIVE ADD-ON" />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faSnowflake} />
                Add Festive Afternoon Tea Only
              </OptionTitle>
       
              <OptionDescription>
                Perfect if you already have Castle or Gardens entry tickets! Simply add our magical 
                Christmas Afternoon Tea service to your existing booking. Enjoy festive scones, 
                seasonal sandwiches, Christmas cake, and mulled wine in our beautifully decorated Aviary Tearoom.
              </OptionDescription>
              <OptionDetails>
                <DetailItem>
                  <FontAwesomeIcon icon={faClock} />
                  <div>
                    <strong>Times:</strong><br />
                    12:00 - 16:00
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faUsers} />
                  <div>
                    <strong>Duration:</strong><br />
                    Festive leisure
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faTicketAlt} />
                  <div>
                    <strong>Festive Price:</strong><br />
                    Adult: £35 each <br /> Child: £15 each
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <div>
                    <strong>Requirement:</strong><br />
                    Entry ticket needed
                  </div>
                </DetailItem>
              </OptionDetails>
              <BookButton 
                href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=bc7ebb7a-9607-415d-98c3-96bc63970419" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Add Festive Tea <FontAwesomeIcon icon={faGift} />
              </BookButton>
            </OptionContent>
          </OptionCard>

          <OptionCard>
            <OptionImage image={castleImage} badge="CHRISTMAS PACKAGE" />
            <OptionContent>
              <OptionTitle>
                <FontAwesomeIcon icon={faStar} />
                Entry Ticket + Festive Afternoon Tea
              </OptionTitle>

              <OptionDescription>
                Don't have entry tickets yet? Choose this option for full Castle & Gardens access 
                plus our enchanting Christmas Afternoon Tea service. The complete festive Belvoir 
                experience - explore the Christmas-decorated castle and then enjoy festive afternoon tea.
              </OptionDescription>
              <OptionDetails>
                <DetailItem>
                  <FontAwesomeIcon icon={faCrown} />
                  <div>
                    <strong>Includes:</strong><br />
                    Castle & Gardens
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faSnowflake} />
                  <div>
                    <strong>Plus:</strong><br />
                    Festive Afternoon Tea
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faClock} />
                  <div>
                    <strong>Tea Times:</strong><br />
                    12:00 - 16:00
                  </div>
                </DetailItem>
                <DetailItem>
                  <FontAwesomeIcon icon={faGift} />
                  <div>
                    <strong>Value:</strong><br />
                    Complete festive package
                  </div>
                </DetailItem>
              </OptionDetails>
              <BookButton 
                href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=f7be8040-b3de-43e8-a064-b173b1a8d761" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Book Christmas Package <FontAwesomeIcon icon={faArrowRight} />
              </BookButton>
            </OptionContent>
          </OptionCard>
        </OptionsGrid>

        <InfoBox>
          <InfoText>
            <FontAwesomeIcon /> Festive Afternoon Tea is served daily from 12:00pm to 4:00pm in our Christmas-decorated Aviary Tearoom.

            Advance booking is essential during the festive season.
          </InfoText>
          <InfoText>
            <strong>🎄 Create magical Christmas memories - indulge in our enchanting festive afternoon tea experience! 🎁</strong>
          </InfoText>
        </InfoBox>

        {/* Additional Options */}
        <SectionTitle style={{ marginTop: '4rem' }}>More Festive Dining Options</SectionTitle>
        <IntroText>
          Looking for other Christmas dining experiences? Explore our Aviary Tearoom's festive menu with 
          seasonal lunches, mulled wine, Christmas treats, and holiday beverages available throughout the day.
        </IntroText>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <BookButton href="/castle-dining/aviary-tearoom">
            Visit Aviary Tearoom <FontAwesomeIcon icon={faArrowRight} />
          </BookButton>
        </div> 
      </ContentContainer>
      
      <Footer />
    </AfternoonTeaContainer>
  );
};

export default NewsletterFestiveAfternoonTea; 